import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';

import { Drawer, Input, Pagination, Select, Spin, Tag } from 'antd';
import { collection, orderBy, query } from 'firebase/firestore';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ActionButton from '@src/components/Buttons/ActionButton';
import FilterContainer from '@src/components/FilterContainer';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import StepIndicator from '@src/components/StepIndicator';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumns } from '@src/components/Table';
import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type {
  IWorkJourneyManagementUser,
  IWorkJourneyReplacementCandidate,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { getFullUrl, useFirestoreRealtimeBatched } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import {
  formatAttendanceIdentifierDisplay,
  getOrganizationCountry,
  type OrganizationCountrySource,
} from '@src/packages/utils/nationalId';
import { PATHS } from '@src/packages/workJourney/constants';
import JurisdictionIdPromptModal from '@src/packages/workJourney/components/JurisdictionIdPromptModal';
import IntlMessages from '@src/util/IntlMessages';

import { formatCoverUserDisplayName } from '../formatCoverUserDisplayName';
import { CoverCurrentJourneyLabel } from '../CoverCurrentJourneyLabel';
import { loadOrganizationUsersByIds } from '../loadOrganizationUsersByIds';
import {
  mergeCoverUsersWithOrgProfiles,
  needsCoverUserProfileHydration,
} from '../mergeCoverUsersWithOrgProfiles';
import type {
  IWorkJourneyCoverAbsenceReplaceDrawerParams,
  IWorkJourneyCoverDrawerLocationState,
} from '../types';
import { WorkJourneyCoverReplaceConfirmModal } from './components/WorkJourneyCoverReplaceConfirmModal';
import { WorkJourneyCoverReplaceStep2 } from './components/WorkJourneyCoverReplaceStep2';
import { useWorkJourneyCoverAbsenceReplace } from './hooks/useWorkJourneyCoverAbsenceReplace';
import styles from './styles.module.less';
import {
  toCoverReplaceUserRowFromCandidate,
  toCoverReplaceUserRowFromManagementUser,
} from './types';
import { WorkJourneyManagementContext } from '../../../context';

type UserJobOption = {
  id: string;
  name: string;
};

type StepIndicatorProps = {
  currentStep: number;
  totalSteps: number;
  stepTitles?: string[];
  stepCounterId?: string;
};

const CoverStepIndicator = StepIndicator as FC<StepIndicatorProps>;

export interface WorkJourneyCoverAbsenceReplaceDrawerProps {
  open?: boolean;
  onClose?: () => void;
  onRegistered?: () => void | Promise<void>;
  workJourneyConfigId?: string;
  date?: string;
  absentUserId?: string;
  journeyName?: string;
  currentMembers?: number;
  requiredMembers?: number;
  absentUsers?: IWorkJourneyManagementUser[];
  replacedUser?: IWorkJourneyManagementUser | null;
}

export const WorkJourneyCoverAbsenceReplaceDrawer = ({
  open,
  onClose,
  onRegistered,
  workJourneyConfigId: workJourneyConfigIdProp,
  date: dateProp,
  absentUserId: absentUserIdProp,
  journeyName: journeyNameProp,
  currentMembers: currentMembersProp,
  requiredMembers: requiredMembersProp,
  absentUsers: absentUsersProp,
  replacedUser: replacedUserProp,
}: WorkJourneyCoverAbsenceReplaceDrawerProps) => {
  const intl = useIntl();
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const url = routeMatch?.url ?? '';
  const {
    workJourneyConfigId: routeWorkJourneyConfigId,
    date: routeDate,
    absentUserId: routeAbsentUserId,
  } =
    useParams<IWorkJourneyCoverAbsenceReplaceDrawerParams>();
  const location = useLocation<IWorkJourneyCoverDrawerLocationState | undefined>();
  const {
    currentMembers: locationCurrentMembers,
    requiredMembers: locationRequiredMembers,
    absentUsers: locationAbsentUsers = [],
    replacedUser: locationReplacedUser,
    journeyName: locationJourneyName,
    dateFrom: locationDateFrom,
    dateTo: locationDateTo,
  } = location.state ?? {};
  const workJourneyConfigId = workJourneyConfigIdProp ?? routeWorkJourneyConfigId;
  const date = dateProp ?? routeDate;
  const absentUserId = absentUserIdProp ?? routeAbsentUserId;
  const currentMembers = currentMembersProp ?? locationCurrentMembers;
  const requiredMembers = requiredMembersProp ?? locationRequiredMembers;
  const absentUsers = absentUsersProp ?? locationAbsentUsers;
  const replacedUserFromPropsOrState = replacedUserProp ?? locationReplacedUser;
  const journeyName = journeyNameProp ?? locationJourneyName;
  /** Prefer attention-row Desde so step-2 matches the list (not a hardcoded +2 days end). */
  const coverDateFrom = locationDateFrom || date;
  const coverDateTo = locationDateTo || locationDateFrom || date;
  const managementContext = useContext(WorkJourneyManagementContext);

  const { organizationId, organizationCountry } = useSelector(
    ({
      organizations,
    }: {
      organizations: {
        organization: OrganizationCountrySource & { id?: string };
      };
    }) => ({
      organizationId: organizations.organization?.id,
      organizationCountry: getOrganizationCountry(organizations.organization),
    }),
  );

  const [replacedUser, setReplacedUser] = useState(replacedUserFromPropsOrState ?? null);

  useEffect(() => {
    setReplacedUser(replacedUserFromPropsOrState ?? null);
  }, [replacedUserFromPropsOrState]);

  useEffect(() => {
    const seedUsers = [
      ...(replacedUserFromPropsOrState ? [replacedUserFromPropsOrState] : []),
      ...absentUsers,
    ];
    const idsNeedingHydration = seedUsers
      .filter(needsCoverUserProfileHydration)
      .map(user => user.id)
      .filter(Boolean);
    if (!organizationId || idsNeedingHydration.length === 0) {
      return undefined;
    }

    let cancelled = false;
    void loadOrganizationUsersByIds(organizationId, idsNeedingHydration).then(profilesById => {
      if (cancelled || profilesById.size === 0) return;
      setReplacedUser(current => {
        if (!current) return current;
        return mergeCoverUsersWithOrgProfiles([current], profilesById)[0] ?? current;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [absentUsers, organizationId, replacedUserFromPropsOrState]);

  const excludeUserIds = useMemo(() => {
    const ids = new Set<string>();
    if (absentUserId) ids.add(absentUserId);
    absentUsers.forEach(user => {
      if (user.id) ids.add(user.id);
    });
    if (replacedUser?.id) ids.add(replacedUser.id);
    return Array.from(ids);
  }, [absentUserId, absentUsers, replacedUser?.id]);

  const handleRegistered = useCallback(async () => {
    if (onRegistered) {
      await onRegistered();
      return;
    }

    await managementContext?.refreshActiveTab();
    history.push(getFullUrl(PATHS.WORK_JOURNEY_MANAGEMENT, url));
  }, [history, managementContext, onRegistered, url]);

  const {
    users,
    total,
    loading,
    search,
    jobId,
    currentPage,
    pageSize,
    selectedUser,
    confirmOpen,
    confirmLoading,
    confirmImpact,
    step,
    step2Variant,
    modificationForm,
    submitting,
    setSearch,
    setJobId,
    handlePageChange,
    handleSelectUser,
    pendingJurisdictionUser,
    organizationCountry: hookOrganizationCountry,
    handleConfirmJurisdictionId,
    handleCancelJurisdictionPrompt,
    handleContinue,
    handleConfirmSelect,
    handleChooseAnother,
    handleBackToStep1,
    setDurationMode,
    setStartDate,
    setEndDate,
    setComment,
    canSubmitStep2,
    handleRegisterChange,
    allowedRange,
    allowPermanent,
  } = useWorkJourneyCoverAbsenceReplace({
    excludeUserIds,
    coverWorkJourneyConfigId: workJourneyConfigId,
    coverDate: coverDateFrom,
    coverDateTo,
    absentUserId,
    replacedUser,
    onRegistered: handleRegistered,
  });

  const jobsQueryList = useMemo(() => {
    if (!organizationId) return [];
    return [
      query(
        collection(db, FSCollections.ORGANIZATIONS, organizationId, FSCollections.USER_JOBS),
        orderBy('createdAt', 'desc'),
      ),
    ];
  }, [organizationId]);

  const { data: jobs = [] } = useFirestoreRealtimeBatched(jobsQueryList, [organizationId]);

  const jobOptions = useMemo(
    () =>
      (jobs as UserJobOption[]).map(job => ({
        label: job.name,
        value: job.id,
      })),
    [jobs],
  );

  const neededCount = Math.max(absentUsers.length, 1);
  const selectedCount = selectedUser ? 1 : 0;
  const noJourneyLabel = intl.formatMessage({
    id: 'workJourney.management.cover.replace.noJourney',
  });

  const replacedUserRow = useMemo(() => {
    const source =
      replacedUser || absentUsers.find(user => user.id === absentUserId) || absentUsers[0];
    if (!source) return null;
    return toCoverReplaceUserRowFromManagementUser(
      source,
      journeyName || '—',
      Boolean(journeyName?.trim()),
    );
  }, [absentUserId, absentUsers, journeyName, replacedUser]);

  const replacementUserRow = useMemo(() => {
    if (!selectedUser) return null;
    return toCoverReplaceUserRowFromCandidate(selectedUser, noJourneyLabel);
  }, [noJourneyLabel, selectedUser]);

  const handleClose = () => {
    if (step === 2) {
      handleBackToStep1();
      return;
    }
    if (onClose) {
      onClose();
      return;
    }
    history.goBack();
  };

  const drawerTitleId =
    step === 2 && step2Variant === 'assign'
      ? 'workJourney.management.cover.replace.step2.assignTitle'
      : step === 2
        ? 'workJourney.management.cover.replace.step2.replaceTitle'
        : 'workJourney.management.cover.replace.title';

  const primaryAction =
    step === 1
      ? {
          label: intl.formatMessage({
            id: 'workJourney.management.cover.replace.continue',
          }),
          type: 'primary' as const,
          action: handleContinue,
          disabled: !selectedUser || confirmLoading,
          allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
        }
      : {
          label: intl.formatMessage({
            id: 'workJourney.management.cover.replace.step2.register',
          }),
          type: 'primary' as const,
          action: handleRegisterChange,
          disabled: !canSubmitStep2 || submitting,
          loading: submitting,
          allowedRole: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
        };

  const step2AlertDescription = intl.formatMessage(
    { id: 'workJourney.management.cover.absence.alertDescription' },
    {
      current: currentMembers ?? '—',
      required: requiredMembers ?? '—',
    },
  );

  const usersBeingReplaced = useMemo(() => {
    if (replacedUser) return [replacedUser];
    const matched = absentUsers.find(user => user.id === absentUserId);
    if (matched) return [matched];
    return absentUsers.slice(0, 1);
  }, [absentUserId, absentUsers, replacedUser]);

  const replacedUserColumns: ResponsiveTableColumns<IWorkJourneyManagementUser> = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'workJourney.management.cover.columns.user' }),
        key: 'user',
        render: user => (
          <div className={styles.userCell}>
            <MemberAvatar size={32} user={user} />
            <span>{formatCoverUserDisplayName(user)}</span>
          </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'workJourney.management.cover.columns.idCard' }),
        key: 'idCard',
        render: user =>
          formatAttendanceIdentifierDisplay(organizationCountry, user) || '—',
      },
      {
        title: intl.formatMessage({ id: 'workJourney.management.cover.columns.status' }),
        key: 'status',
        render: () => (
          <Tag color="gold" className="gx-mb-0">
            <IntlMessages id="workJourney.management.cover.absence.status" />
          </Tag>
        ),
      },
    ],
    [intl, organizationCountry],
  );

  const columns: ResponsiveTableColumns<IWorkJourneyReplacementCandidate> = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'workJourney.management.cover.columns.user' }),
        key: 'user',
        render: user => (
          <div className={styles.userCell}>
            <MemberAvatar size={32} user={user} />
            <span>{formatCoverUserDisplayName(user)}</span>
          </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'workJourney.management.cover.columns.idCard' }),
        key: 'idCard',
        render: user =>
          formatAttendanceIdentifierDisplay(organizationCountry, user) || '—',
      },
      {
        title: intl.formatMessage({
          id: 'workJourney.management.cover.replace.columns.currentJourney',
        }),
        key: 'currentJourney',
        render: user => (
          <CoverCurrentJourneyLabel
            journeyName={user.currentWorkJourney?.workJourneyConfigName}
          />
        ),
      },
      {
        title: '',
        key: 'action',
        align: 'right',
        render: user => {
          const isSelected = selectedUser?.id === user.id;
          return (
            <ActionButton.Container
              buttons={[
                <ActionButton
                  label={intl.formatMessage({
                    id: isSelected
                      ? 'workJourney.management.cover.replace.selected'
                      : 'workJourney.management.cover.replace.select',
                  })}
                  onClick={() => handleSelectUser(user)}
                  type="primary"
                  color="primary"
                  size="medium"
                  allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
                  disabledScaling
                />,
              ]}
            />
          );
        },
      },
    ],
    [handleSelectUser, intl, organizationCountry, selectedUser?.id],
  );

  return (
    <Drawer
      open={open ?? Boolean(workJourneyConfigId && date && absentUserId)}
      width="100%"
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      onClose={handleClose}
    >
      <BoxContainer>
        <BoxContainer fullContent fixed bottomBorder>
          <FilterContainer
            goBack={handleClose}
            title={<Title.Header value={<IntlMessages id={drawerTitleId} />} />}
            actionButtons={[primaryAction]}
          />
        </BoxContainer>

        <BoxContainer fullContent>
          <CoverStepIndicator
            currentStep={step}
            totalSteps={2}
            stepCounterId="workJourney.management.cover.replace.step"
          />

          {step === 1 ? (
            <>
              {usersBeingReplaced.length > 0 && (
                <>
                  <Title.SubTitle
                    className="gx-mt-4 gx-mb-3"
                    value={
                      <IntlMessages id="workJourney.management.cover.replace.step2.replacedUserSection" />
                    }
                  />
                  <ResponsiveTable dataSource={usersBeingReplaced} columns={replacedUserColumns} />
                </>
              )}

              <Title.SubTitle
                className="gx-mt-4 gx-mb-3"
                value={<IntlMessages id="workJourney.management.cover.replace.selectSection" />}
              />

              <div className={styles.filtersRow}>
                <Input.Search
                  className={`${styles.searchInput} gx-mb-0`}
                  allowClear
                  value={search}
                  placeholder={intl.formatMessage({
                    id: 'workJourney.management.cover.replace.searchPlaceholder',
                  })}
                  onChange={event => setSearch(event.target.value)}
                />
                <Select
                  className={styles.jobSelect}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={jobId}
                  placeholder={intl.formatMessage({
                    id: 'workJourney.management.cover.replace.jobPlaceholder',
                  })}
                  options={jobOptions}
                  onChange={value => setJobId(value || undefined)}
                />
                <span className={styles.counter}>
                  {selectedCount}/{neededCount}
                </span>
              </div>

              <Spin spinning={loading}>
                <ResponsiveTable dataSource={users} columns={columns} />
              </Spin>

              {total > pageSize && (
                <div className={styles.pagination}>
                  <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={total}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          ) : (
            <WorkJourneyCoverReplaceStep2
              variant={step2Variant}
              organizationCountry={hookOrganizationCountry ?? organizationCountry}
              form={modificationForm}
              replacedUserRow={replacedUserRow}
              replacementUserRow={replacementUserRow}
              alertTitle={<IntlMessages id="workJourney.management.cover.absence.alertTitle" />}
              alertDescription={step2AlertDescription}
              allowedRange={allowedRange}
              allowPermanent={allowPermanent}
              onDurationModeChange={setDurationMode}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onCommentChange={setComment}
            />
          )}
        </BoxContainer>
      </BoxContainer>

      <WorkJourneyCoverReplaceConfirmModal
        open={confirmOpen}
        loading={confirmLoading}
        impact={confirmImpact}
        onConfirm={handleConfirmSelect}
        onChooseAnother={handleChooseAnother}
      />
      <JurisdictionIdPromptModal
        open={!!pendingJurisdictionUser}
        organizationCountry={hookOrganizationCountry ?? organizationCountry}
        personalCountry={pendingJurisdictionUser?.address?.country}
        personalIdCard={pendingJurisdictionUser?.idCard}
        userName={pendingJurisdictionUser?.displayName}
        onCancel={handleCancelJurisdictionPrompt}
        onConfirm={handleConfirmJurisdictionId}
      />
    </Drawer>
  );
};
